/**
 * `useReferenceDocExtractionPolling` — advance document statuses while
 * they are still working (#2294).
 *
 * Reading a document is asynchronous: the server queues the work and
 * answers immediately, so the row lands on `pending`, moves to
 * `extracting`, and settles on `done` or `failed` minutes later. Nothing
 * pushes those changes to the browser, so something has to ask.
 *
 * This loop existed before, inline in the Applications page, from when
 * documents lived on an Applications tab. #2158 Slice 2 moved documents
 * to Tests and retired that tab, and the loop did not come along. For ten
 * days it ran on a page with no document UI, refreshing an application
 * list to update a pill it no longer drew, while the page that did draw
 * the pill had nothing driving it. Both halves looked fine in isolation,
 * which is why it took so long to spot.
 *
 * A hook rather than an effect back inside a page, even with one caller
 * and no second one planned. Reuse is not the reason. The reason is that
 * an inline effect in a 500-line page is exactly where the last one hid,
 * and that the rules below deserve to be tested directly with a fake
 * timer, which an inline effect cannot be.
 */
import { useEffect, useMemo, useRef } from 'react'
import type { ReferenceDocExtractionStatus } from '@/types'

/** How often to ask. Three seconds is what the original loop used: fast
 * enough that a document finishing feels immediate, slow enough that the
 * request is not noticeable. */
const POLL_INTERVAL_MS = 3000

/** The statuses that mean "still working", and so the only ones worth
 * asking again about. Defined once, here, rather than in each caller:
 * a page keeping its own copy of this list is a page that stops polling
 * early the day a status is added. `undefined` counts as finished, which
 * is what a document uploaded before the field existed carries. */
const IN_PROGRESS: readonly ReferenceDocExtractionStatus[] = ['pending', 'extracting', 'retrying']

function stillWorking(status: ReferenceDocExtractionStatus | undefined): boolean {
  return status !== undefined && IN_PROGRESS.includes(status)
}

/**
 * Call `refresh` every few seconds while any of `docs` is still being
 * read. Stops as soon as they have all settled, and never starts when
 * there is nothing to watch.
 *
 * `refresh` is held in a ref and deliberately NOT a dependency of the
 * timer. Callers pass an inline arrow closing over the app on screen, so
 * its identity changes every render. Depending on it would tear the
 * interval down and rebuild it each time, which polls harder than
 * intended while looking correct; ignoring it entirely would leave the
 * timer calling a stale closure and refreshing the app the user has just
 * navigated away from. The ref gives the latest callback to a timer that
 * only restarts when there is genuinely something new to watch.
 */
export function useReferenceDocExtractionPolling(
  docs: ReadonlyArray<{ extractionStatus?: ReferenceDocExtractionStatus }> | undefined,
  refresh: () => void,
): void {
  const refreshRef = useRef(refresh)
  // Assigned on every render, so a tick always reaches the newest
  // callback. Not in an effect: an effect would leave one tick able to
  // fire against the previous render's closure.
  refreshRef.current = refresh

  // A boolean, not the array, so the timer survives the new array
  // identity a refresh produces on every poll. Depending on `docs`
  // itself would restart the interval on each tick and never let it run
  // a full period.
  const anyWorking = useMemo(
    () => (docs ?? []).some(d => stillWorking(d.extractionStatus)),
    [docs],
  )

  useEffect(() => {
    if (!anyWorking) return
    const id = setInterval(() => { refreshRef.current() }, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [anyWorking])
}
